import { useEffect, useState } from 'react';
import { useSearchParams, Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi, type BusinessSignupRequest } from '@/api/auth';
import { getApiErrorMessage } from '@/utils/apiError';
import { lookupsApi } from '@/api/lookups';
import type { City } from '@/types';
import { normalizePhoneInput } from '@/utils/phone';
import { apiClient } from '@/api/client';

type Segment = 'individual' | 'team' | 'salon' | 'shop';
type ProviderType = 0 | 1 | 2;
type SignupContext = { tenantName?: string; enabledModules?: string[]; phonePrefix?: string | null;
  tenantMetaStatus?: 'loading' | 'ready' | 'error'; retryTenantMeta?: () => void };

const PROVIDER_TYPE_OPTIONS: { v: ProviderType; key: string }[] = [
  { v: 0, key: 'individual' },
  { v: 2, key: 'soloBusiness' },
  { v: 1, key: 'teamBusiness' },
];

const SEGMENT_OPTIONS: { v: Segment; emoji: string }[] = [
  { v: 'individual', emoji: '🛠️' },
  { v: 'team', emoji: '🚚' },
  { v: 'salon', emoji: '📅' },
  { v: 'shop', emoji: '🛒' },
];

/// Phase 3.R — self-serve provider signup. Lands at the canonical /join route;
/// /admin-signup remains a backward-compatible alias. Lets the
/// landing pages' "Start free" CTA finally do something other than redirect
/// to admin login. Pre-fills the segment from the `?segment=` query param so
/// salon visitors who clicked the salon landing don't have to pick again.
export function BusinessSignupPage() {
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const parentContext = useOutletContext<SignupContext>() || {};
  const [ownContext, setOwnContext] = useState<SignupContext>({});
  const [ownMetaStatus, setOwnMetaStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [metaAttempt, setMetaAttempt] = useState(0);
  const usesParentMetadata = parentContext.tenantMetaStatus !== undefined || parentContext.enabledModules !== undefined;
  const context = usesParentMetadata ? parentContext : ownContext;
  const metaStatus = context.tenantMetaStatus ?? (context.enabledModules ? 'ready' : ownMetaStatus);
  const retryMetadata = context.retryTenantMeta ?? (() => setMetaAttempt(value => value + 1));
  useEffect(() => {
    if (usesParentMetadata) return;
    let active = true;
    setOwnMetaStatus('loading');
    apiClient.get<{ name?: string; enabledModules?: string[]; phonePrefix?: string }>('/tenants/meta').then(({ data }) => {
      if (!active) return;
      setOwnContext({ tenantName: data.name, enabledModules: data.enabledModules ?? [], phonePrefix: data.phonePrefix });
      setOwnMetaStatus('ready');
    }).catch(() => { if (active) setOwnMetaStatus('error'); });
    return () => { active = false; };
  }, [usesParentMetadata, metaAttempt]);
  const segmentOptions = SEGMENT_OPTIONS.filter(({ v }) => {
    if (!context.enabledModules) return true;
    if (v === 'salon') return context.enabledModules.some((key) => key === 'booking' || key === 'online_services');
    if (v === 'shop') return context.enabledModules.includes('shop');
    return context.enabledModules.includes('home_services');
  });

  const segmentFromUrl = searchParams.get('segment') as Segment | null;
  const initialSegment: Segment | null =
    segmentFromUrl && SEGMENT_OPTIONS.some((o) => o.v === segmentFromUrl) ? segmentFromUrl : null;

  const [segment, setSegment] = useState<Segment | null>(initialSegment);
  const [providerType, setProviderType] = useState<ProviderType | null>(
    initialSegment === 'team' ? 1 : initialSegment === 'salon' || initialSegment === 'shop' ? 2 : initialSegment === 'individual' ? 0 : null,
  );
  const selectedSegment = segmentOptions.length === 1 ? segmentOptions[0].v : segment;
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mobile, setMobile] = useState('');
  const [cities, setCities] = useState<City[]>([]);
  const [cityId, setCityId] = useState(0);
  const [cityStatus, setCityStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [cityAttempt, setCityAttempt] = useState(0);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [emailDeliveryQueued, setEmailDeliveryQueued] = useState(true);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'requested' | 'error'>('idle');
  const [deliveryError, setDeliveryError] = useState('');

  useEffect(() => {
    let active = true;
    setCityStatus('loading');
    lookupsApi.getCities().then(values => {
      if (!active) return;
      const available = values.filter(city => city.id > 0 && city.isActive !== false);
      setCities(available); setCityStatus('ready');
      setCityId(current => available.some(city => city.id === current) ? current : 0);
    }).catch(() => { if (active) setCityStatus('error'); });
    return () => { active = false; };
  }, [cityAttempt]);

  // Pre-fill segment from URL once on mount (idempotent — won't reset if user changes it).
  useEffect(() => {
    if (!segment && initialSegment) setSegment(initialSegment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const text = {
    title: t('businessSignup.title', { brand: context.tenantName || t('auth.login.portalName') }),
    subtitle: t('businessSignup.subtitle'),
    pickSegment: t('businessSignup.pickSegment'),
    pickProviderType: t('businessSignup.pickProviderType'),
    businessName: t('businessSignup.businessName'),
    businessNamePh: t('businessSignup.businessNamePlaceholder'),
    email: t('businessSignup.email'),
    emailPh: t('businessSignup.emailPlaceholder'),
    password: t('businessSignup.password'),
    passwordHint: t('businessSignup.passwordHint'),
    mobile: t('businessSignup.mobile'),
    mobilePh: context.phonePrefix ? `+${context.phonePrefix.replace(/\D/g, '')}…` : t('businessSignup.mobilePlaceholder'),
    submit: t('businessSignup.submit'),
    loading: t('businessSignup.loading'),
    haveAccount: t('businessSignup.haveAccount'),
    signIn: t('businessSignup.signIn'),
    needSegment: t('businessSignup.needSegment'),
    needProviderType: t('businessSignup.needProviderType'),
    checkEmailTitle: t('businessSignup.checkEmailTitle'),
    checkEmailBody: t('businessSignup.checkEmailBody'),
    submittedFor: t('businessSignup.submittedFor'),
  };

  const resendConfirmation = async () => {
    if (!submittedEmail || resendState === 'sending') return;
    setResendState('sending'); setDeliveryError('');
    try {
      await authApi.resendVerification(submittedEmail);
      setResendState('requested');
    } catch (error) {
      setResendState('error');
      setDeliveryError(getApiErrorMessage(error, t('signupDelivery.resendFailed')));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!selectedSegment || !segmentOptions.some((option) => option.v === selectedSegment)) {
      setError(text.needSegment);
      return;
    }
    if (providerType === null) {
      setError(text.needProviderType);
      return;
    }
    if (metaStatus !== 'ready' || cityStatus !== 'ready' || !cities.some(city => city.id === cityId)) {
      setError(t('businessSignup.chooseCity'));
      return;
    }
    setIsLoading(true);
    try {
      const payload: BusinessSignupRequest = {
        businessName: businessName.trim(),
        email: email.trim().toLowerCase(),
        password,
        mobile: mobile.trim(),
        segment: selectedSegment,
        providerType,
        cityId,
      };
      const res = await authApi.businessSignup(payload);
      if (!res.registrationPending || !res.emailConfirmationRequired) {
        throw new Error(t('businessSignup.invalidResponse'));
      }
      setEmailDeliveryQueued(res.emailDeliveryQueued !== false);
      setPassword('');
      setSubmittedEmail(payload.email);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, t('businessSignup.failed')));
    } finally {
      setIsLoading(false);
    }
  };

  if (submittedEmail) {
    return (
      <div className="space-y-6 text-center" role="status" aria-live="polite">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-3xl">
          ✉️
        </div>
        <div>
          <h2 className="text-2xl font-bold">{emailDeliveryQueued ? text.checkEmailTitle : t('signupDelivery.pendingTitle')}</h2>
          <p className="mt-3 text-sm text-muted-foreground">{resendState === 'requested' ? t('signupDelivery.resendRequested')
            : emailDeliveryQueued ? text.checkEmailBody : t('signupDelivery.pendingBody')}</p>
          <p className="mt-3 text-sm font-medium" dir="ltr">
            {text.submittedFor}: {submittedEmail}
          </p>
        </div>
        {deliveryError && <p role="alert" className="text-sm text-destructive">{deliveryError}</p>}
        <button type="button" onClick={resendConfirmation} disabled={resendState === 'sending'}
          className="inline-flex w-full items-center justify-center rounded-lg border border-border px-4 py-3 font-medium hover:bg-muted disabled:opacity-50">
          {t(resendState === 'sending' ? 'signupDelivery.resending' : 'signupDelivery.resend')}
        </button>
        <Link
          to="/admin-login"
          className="inline-flex w-full items-center justify-center rounded-lg bg-primary px-4 py-3 font-medium text-primary-foreground hover:bg-primary/90"
        >
          {text.signIn}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold">{text.title}</h2>
        <p className="mt-2 text-muted-foreground text-sm">{text.subtitle}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div role="alert" className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm text-center">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-2">{text.pickProviderType}</label>
          <div className="grid gap-2">
            {PROVIDER_TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.v}
                type="button"
                data-testid={`provider-type-${opt.v}`}
                aria-pressed={providerType === opt.v}
                onClick={() => setProviderType(opt.v)}
                className={`text-start px-3 py-3 rounded-lg border-2 transition-all ${
                  providerType === opt.v
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/40'
                }`}
              >
                <div className="font-bold text-sm">{t(`businessSignup.providerTypes.${opt.key}.label`)}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {t(`businessSignup.providerTypes.${opt.key}.description`)}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Segment picker — pre-filled from URL when the visitor came from a
            segment-specific landing page. */}
        <div>
          <label className="block text-sm font-medium mb-2">{text.pickSegment}</label>
          {metaStatus === 'ready' && segmentOptions.length === 0 && <p role="alert" className="text-sm text-muted-foreground">{t('businessSignup.noActivities')}</p>}
          <div className="grid grid-cols-2 gap-2">
            {segmentOptions.map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setSegment(opt.v)}
                aria-pressed={selectedSegment === opt.v}
                className={`text-start px-3 py-3 rounded-lg border-2 transition-all ${
                  selectedSegment === opt.v
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/40'
                }`}
              >
                <div className="text-2xl mb-1">{opt.emoji}</div>
                <div className="font-bold text-sm">{t(`businessSignup.segments.${opt.v}.label`)}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {t(`businessSignup.segments.${opt.v}.description`)}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="bizname" className="block text-sm font-medium">{text.businessName}</label>
          <input
            id="bizname"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            required
            placeholder={text.businessNamePh}
            className="w-full px-4 py-3 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {metaStatus !== 'ready' && <div role={metaStatus === 'error' ? 'alert' : 'status'} className="text-sm text-muted-foreground">
          <p>{t(metaStatus === 'error' ? 'businessSignup.metaFailed' : 'businessSignup.metaLoading')}</p>
          {metaStatus === 'error' && <button type="button" onClick={retryMetadata}>{t('common.retry')}</button>}
        </div>}
        <div className="space-y-2">
          <label htmlFor="business-city" className="block text-sm font-medium">{t('businessSignup.operatingCity')}</label>
          <select id="business-city" value={cityId || ''} required disabled={cityStatus !== 'ready' || cities.length === 0 || isLoading}
            onChange={event => setCityId(Number(event.target.value))} aria-describedby="business-city-hint"
            className="w-full px-4 py-3 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary">
            <option value="">{t('businessSignup.chooseCity')}</option>
            {cities.map(city => <option key={city.id} value={city.id}>
              {i18n.language.startsWith('ar') ? city.arabicName || city.englishName : city.englishName || city.arabicName}
            </option>)}
          </select>
          <div id="business-city-hint" className="text-xs text-muted-foreground">
            {cityStatus === 'loading' && <p role="status">{t('businessSignup.loadingCities')}</p>}
            {cityStatus === 'error' && <p role="alert">{t('businessSignup.citiesFailed')}</p>}
            {cityStatus === 'ready' && cities.length === 0 && <p role="alert">{t('businessSignup.citiesEmpty')}</p>}
            {(cityStatus === 'error' || cityStatus === 'ready' && cities.length === 0) &&
              <button type="button" onClick={() => setCityAttempt(value => value + 1)}>{t('common.retry')}</button>}
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="email" className="block text-sm font-medium">{text.email}</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder={text.emailPh}
            dir="ltr"
            className="w-full px-4 py-3 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="mobile" className="block text-sm font-medium">{text.mobile}</label>
          <input
            id="mobile"
            type="tel"
            autoComplete="tel"
            value={mobile}
            onChange={(e) => setMobile(normalizePhoneInput(e.target.value))}
            required
            placeholder={text.mobilePh}
            dir="ltr"
            className="w-full px-4 py-3 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="password" className="block text-sm font-medium">{text.password}</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={12}
            dir="ltr"
            className="w-full px-4 py-3 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-xs text-muted-foreground">{text.passwordHint}</p>
        </div>

        <button
          type="submit"
          disabled={isLoading || !selectedSegment || providerType === null || metaStatus !== 'ready' || cityStatus !== 'ready' || !cityId}
          className="w-full py-3 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {isLoading ? text.loading : text.submit}
        </button>

        <div className="text-center text-sm text-muted-foreground">
          {text.haveAccount}{' '}
          <Link to="/admin-login" className="text-primary hover:underline font-medium">
            {text.signIn}
          </Link>
        </div>
      </form>
    </div>
  );
}
